import {
  disconnectSession,
  fetchSessionsForConflict,
  filterLiveDeviceSessions,
  findConflictingSessions,
  getOrCreateDriverSessionId,
  HEARTBEAT_INTERVAL_MS,
  mergeSessionsWithOptimisticSelf,
  normalizeHeartbeatResponse,
  postHeartbeat,
  type ConflictCheckerContext,
  type DeviceSessionVO,
  type HeartbeatParams,
} from "@eweadn-qiankun/shared-http";
import { xingshanRawServiceKeyboard } from "../../sdkApi/keyboard/commonApi";
import {
  notifyDeviceSessionPresenceChanged,
  useDeviceSessionPresenceStore,
  type DeviceSessionRole,
} from "../../stores/deviceSessionPresenceStore";
import { buildXingshanHeartbeatParams } from "./buildHeartbeatContext";
import { applyDeviceSessionRoleTransition } from "./ownerSessionController";
import { setDeviceSessionStandbyMode } from "./standbyGuard";
import {
  getLocalDeviceSessions,
  postLocalDeviceSessionHeartbeat,
  removeLocalDeviceSession,
} from "./localSessionRegistry";
import {
  initDriverWindowFocusTracker,
  isDriverWindowFocused,
} from "./driverWindowFocus";

/** 避免双端/双页签在边界态反复 close/init HID，导致灯效越切越乱 */
const OWNER_CLAIM_COOLDOWN_MS = 1200;
/** focus/blur/visibility 常连发，合并为一次同步 */
const FOCUS_SYNC_DEBOUNCE_MS = 400;

/**
 * 主应用 + 子应用 webpack 会各自打一份本模块；定时器必须挂 window，否则会出现双心跳。
 */
type PresenceRuntime = {
  heartbeatTimer: ReturnType<typeof setInterval> | null;
  activeSessionId: string | null;
  activeCtx: ConflictCheckerContext | null;
  buildParamsFn: (() => Promise<HeartbeatParams | null>) | null;
  unloadHandlersInstalled: boolean;
  focusHandlersInstalled: boolean;
  lastAppliedRole: DeviceSessionRole;
  syncInFlight: boolean;
  syncPending: boolean;
  lastRoleChangedAt: number;
  startInFlight: Promise<void> | null;
  focusSyncTimer: ReturnType<typeof setTimeout> | null;
};

declare global {
  interface Window {
    __eweadn_deviceSessionPresenceRuntime?: PresenceRuntime;
  }
}

function createPresenceRuntime(): PresenceRuntime {
  return {
    heartbeatTimer: null,
    activeSessionId: null,
    activeCtx: null,
    buildParamsFn: null,
    unloadHandlersInstalled: false,
    focusHandlersInstalled: false,
    lastAppliedRole: "offline",
    syncInFlight: false,
    syncPending: false,
    lastRoleChangedAt: 0,
    startInFlight: null,
    focusSyncTimer: null,
  };
}

/** SSR / 非浏览器：退回模块级单例 */
const moduleRuntime = createPresenceRuntime();

function getRuntime(): PresenceRuntime {
  if (typeof window === "undefined") return moduleRuntime;
  if (!window.__eweadn_deviceSessionPresenceRuntime) {
    window.__eweadn_deviceSessionPresenceRuntime = createPresenceRuntime();
  }
  return window.__eweadn_deviceSessionPresenceRuntime;
}

function isLoggedIn(): boolean {
  try {
    return !!localStorage.getItem("token");
  } catch {
    return false;
  }
}

async function applyRoleIfNeeded(
  nextRole: "owner" | "standby"
): Promise<boolean> {
  const rt = getRuntime();
  const prev = rt.lastAppliedRole;
  if (nextRole === prev) return true;

  const now = Date.now();
  if (
    nextRole === "owner" &&
    prev === "standby" &&
    now - rt.lastRoleChangedAt < OWNER_CLAIM_COOLDOWN_MS
  ) {
    return false;
  }

  const handoffOk = await applyDeviceSessionRoleTransition(nextRole, prev);
  if (nextRole === "owner" && !handoffOk) {
    console.warn(
      "[device-session] Owner handoff failed; remain standby until next retry"
    );
    return false;
  }

  rt.lastAppliedRole = nextRole;
  rt.lastRoleChangedAt = now;
  setDeviceSessionStandbyMode(nextRole === "standby");
  return true;
}

async function syncPresenceFromNetworkCore(): Promise<void> {
  const rt = getRuntime();
  if (!rt.activeCtx || !rt.activeSessionId) return;

  let params: HeartbeatParams | null = null;
  if (rt.buildParamsFn) {
    params = await rt.buildParamsFn();
  }
  if (!params) return;

  rt.activeCtx = {
    sessionId: params.sessionId,
    deviceKey: params.deviceKey,
    sn: params.sn ?? null,
    vid: params.vid ?? null,
    pid: params.pid ?? null,
    isLoggedIn: isLoggedIn(),
  };

  const heartbeatAt = new Date().toISOString();
  postLocalDeviceSessionHeartbeat(params, heartbeatAt);

  let raw: unknown = null;
  let remoteSessions: DeviceSessionVO[] = [];
  try {
    raw = await postHeartbeat(params);
    remoteSessions = await fetchSessionsForConflict(rt.activeCtx);
  } catch (e) {
    console.warn(
      "[device-session] remote presence unavailable, using local tab registry",
      e
    );
  }

  const localSessions = getLocalDeviceSessions();
  const liveRemote = filterLiveDeviceSessions([
    ...remoteSessions,
    ...localSessions,
  ]);
  const sessions = mergeSessionsWithOptimisticSelf(
    liveRemote,
    rt.activeCtx,
    params,
    heartbeatAt
  );
  const normalized = normalizeHeartbeatResponse(raw, sessions, rt.activeCtx, {
    hasFocus: isDriverWindowFocused(),
  });
  const sameDeviceConflicts = findConflictingSessions(
    filterLiveDeviceSessions(sessions),
    rt.activeCtx
  );

  let effectiveRole = normalized.role;
  if (normalized.role === "owner" || normalized.role === "standby") {
    const handoffOk = await applyRoleIfNeeded(normalized.role);
    if (normalized.role === "owner" && !handoffOk) {
      effectiveRole = "standby";
    }
  }

  useDeviceSessionPresenceStore.getState().setPresence({
    role: effectiveRole,
    sessionId: rt.activeSessionId,
    deviceKey: params.deviceKey,
    ownerSessionId:
      effectiveRole === "owner"
        ? rt.activeSessionId
        : normalized.ownerSessionId,
    ownerClientType:
      effectiveRole === "owner"
        ? (params.clientType ?? normalized.ownerClientType ?? null)
        : (normalized.ownerClientType ?? null),
    conflicts: sameDeviceConflicts,
    lastHeartbeat: { ...normalized, role: effectiveRole },
    isPresenceActive: true,
  });
  notifyDeviceSessionPresenceChanged();
}

async function syncPresenceFromNetwork(): Promise<void> {
  const rt = getRuntime();
  if (rt.syncInFlight) {
    rt.syncPending = true;
    return;
  }

  rt.syncInFlight = true;
  try {
    do {
      rt.syncPending = false;
      try {
        await syncPresenceFromNetworkCore();
      } catch (e) {
        console.warn("[device-session] presence sync failed", e);
      }
    } while (rt.syncPending);
  } finally {
    rt.syncInFlight = false;
  }
}

function clearTimers(): void {
  const rt = getRuntime();
  if (rt.heartbeatTimer != null) {
    clearInterval(rt.heartbeatTimer);
    rt.heartbeatTimer = null;
  }
  if (rt.focusSyncTimer != null) {
    clearTimeout(rt.focusSyncTimer);
    rt.focusSyncTimer = null;
  }
}

function installBeforeUnload(): void {
  const rt = getRuntime();
  if (typeof window === "undefined" || rt.unloadHandlersInstalled) return;
  rt.unloadHandlersInstalled = true;
  const handler = () => {
    try {
      const raw = xingshanRawServiceKeyboard as {
        closeDevice?: () => Promise<unknown>;
      };
      if (typeof raw.closeDevice === "function") {
        void raw.closeDevice().catch(() => {});
      }
    } catch {
      /* ignore */
    }
    const sid = getRuntime().activeSessionId;
    if (sid) {
      void disconnectSession(sid).catch(() => {});
    }
  };
  window.addEventListener("beforeunload", handler);
  window.addEventListener("pagehide", handler);
}

function scheduleFocusPresenceSync(): void {
  const rt = getRuntime();
  if (!rt.activeSessionId) return;
  if (rt.focusSyncTimer != null) {
    clearTimeout(rt.focusSyncTimer);
  }
  rt.focusSyncTimer = setTimeout(() => {
    rt.focusSyncTimer = null;
    if (!getRuntime().activeSessionId) return;
    void syncPresenceFromNetwork();
  }, FOCUS_SYNC_DEBOUNCE_MS);
}

function installFocusHandlersFixed(): void {
  const rt = getRuntime();
  if (typeof window === "undefined" || rt.focusHandlersInstalled) return;
  rt.focusHandlersInstalled = true;
  initDriverWindowFocusTracker();
  window.addEventListener("focus", scheduleFocusPresenceSync, true);
  window.addEventListener("blur", scheduleFocusPresenceSync, true);
  document.addEventListener("visibilitychange", scheduleFocusPresenceSync);
}

function uninstallFocusHandlersFixed(): void {
  const rt = getRuntime();
  if (typeof window === "undefined" || !rt.focusHandlersInstalled) return;
  rt.focusHandlersInstalled = false;
  window.removeEventListener("focus", scheduleFocusPresenceSync, true);
  window.removeEventListener("blur", scheduleFocusPresenceSync, true);
  document.removeEventListener("visibilitychange", scheduleFocusPresenceSync);
}

/** WebHID 连接成功后：心跳同步（含冲突判定），Standby 禁 HID（见 standbyGuard） */
export async function startXingshanDeviceSessionPresence(): Promise<void> {
  if (typeof window === "undefined") return;

  const rt = getRuntime();
  if (rt.startInFlight) {
    await rt.startInFlight;
    if (rt.activeSessionId && rt.heartbeatTimer != null) {
      await syncPresenceFromNetwork();
    }
    return;
  }

  rt.startInFlight = (async () => {
    const sessionId = getOrCreateDriverSessionId();
    if (rt.activeSessionId === sessionId && rt.heartbeatTimer != null) {
      await syncPresenceFromNetwork();
      return;
    }

    await stopDeviceSessionPresenceAsync();

    rt.activeSessionId = sessionId;
    rt.buildParamsFn = () => buildXingshanHeartbeatParams(sessionId);

    const params = await rt.buildParamsFn();
    if (!params) {
      console.warn(
        "[device-session] skip presence: not xingshan or no equipment"
      );
      rt.activeSessionId = null;
      rt.buildParamsFn = null;
      return;
    }

    rt.activeCtx = {
      sessionId,
      deviceKey: params.deviceKey,
      sn: params.sn ?? null,
      vid: params.vid ?? null,
      pid: params.pid ?? null,
      isLoggedIn: isLoggedIn(),
    };

    rt.lastAppliedRole = "offline";

    /** 先挂定时器再首同步，避免并发 start 因 timer==null 再开第二路 */
    rt.heartbeatTimer = setInterval(() => {
      void syncPresenceFromNetwork();
    }, HEARTBEAT_INTERVAL_MS);

    installBeforeUnload();
    installFocusHandlersFixed();

    await syncPresenceFromNetwork();

    if (import.meta.env?.DEV) {
      console.info("[device-session] presence started", {
        deviceKey: params.deviceKey,
      });
    }
  })();

  try {
    await rt.startInFlight;
  } finally {
    rt.startInFlight = null;
  }
}

export function stopDeviceSessionPresence(): void {
  void stopDeviceSessionPresenceAsync();
}

async function releaseXingshanHidBestEffort(): Promise<void> {
  try {
    const raw = xingshanRawServiceKeyboard as {
      closeDevice?: () => Promise<unknown>;
    };
    if (typeof raw.closeDevice === "function") {
      await raw.closeDevice();
    }
  } catch (e) {
    console.warn("[device-session] closeDevice failed", e);
  }
}

/** 退出前完整清理：停轮询、释放 HID、await 注销会话 */
export async function stopDeviceSessionPresenceAsync(options?: {
  releaseHid?: boolean;
}): Promise<void> {
  const rt = getRuntime();
  clearTimers();
  uninstallFocusHandlersFixed();
  const sid = rt.activeSessionId;
  rt.activeSessionId = null;
  rt.activeCtx = null;
  rt.buildParamsFn = null;
  rt.lastAppliedRole = "offline";
  rt.syncPending = false;
  setDeviceSessionStandbyMode(false);

  useDeviceSessionPresenceStore.getState().resetPresence();
  notifyDeviceSessionPresenceChanged();

  if (options?.releaseHid) {
    await releaseXingshanHidBestEffort();
  }
  if (sid) {
    removeLocalDeviceSession(sid);
    try {
      await disconnectSession(sid);
    } catch (e) {
      console.warn("[device-session] disconnectSession failed", e);
    }
  }
}

/** Electron 窗口关闭 / 页面卸载前调用 */
export async function shutdownDeviceSessionForQuit(): Promise<void> {
  await stopDeviceSessionPresenceAsync({ releaseHid: true });
}

export function getActiveDeviceSessionConflicts(): DeviceSessionVO[] {
  return useDeviceSessionPresenceStore.getState().conflicts;
}

export function isDeviceSessionStandby(): boolean {
  const { role, isPresenceActive } = useDeviceSessionPresenceStore.getState();
  return isPresenceActive && role === "standby";
}

export function isDeviceSessionOwner(): boolean {
  const { role, isPresenceActive } = useDeviceSessionPresenceStore.getState();
  return !isPresenceActive || role === "offline" || role === "owner";
}
